import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    CircularProgress,
    Alert,
    IconButton,
    Tooltip,
    Chip,
    LinearProgress
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon
} from '@mui/icons-material';
import * as http from '../../common/http';
import { APIUrl } from '../../common/apiConfig';

interface AttendanceData {
    branch_id: number;
    branch_name: string;
    total_students: number;
    present: number;
    absent: number;
    on_leave: number;
    percentage: number;
    date: string;
}

const DailyAttendanceSummary: React.FC<{ branchId?: number }> = ({ branchId }) => {
    const [data, setData] = useState<AttendanceData[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);
            
            let url = `${APIUrl}dashboard-coaching/daily-attendance`;
            if (branchId) {
                url += `?branch_id=${branchId}`;
            }
            
            const response = await http.get(url);
            if (response && response.status === 'success') {
                setData(response.result);
                setLastRefresh(new Date());
            } else {
                setError('Failed to fetch attendance data');
            }
        } catch (err) {
            console.error('Error fetching attendance:', err);
            setError('Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        
        // Auto-refresh every 5 minutes
        const interval = setInterval(fetchData, 5 * 60 * 1000);
        
        return () => clearInterval(interval);
    }, [branchId]);

    const getAttendanceColor = (percentage: number) => {
        if (percentage >= 90) return '#2e7d32'; // Green
        if (percentage >= 75) return '#ed6c02'; // Orange
        return '#d32f2f'; // Red
    };

    if (loading && data.length === 0) {
        return (
            <Card elevation={2}>
                <CardContent>
                    <Box display="flex" justifyContent="center" p={3}>
                        <CircularProgress />
                    </Box>
                </CardContent>
            </Card>
        );
    }

    if (error) {
        return (
            <Card elevation={2}>
                <CardContent>
                    <Alert severity="error">{error}</Alert>
                </CardContent>
            </Card>
        );
    }

    const totalAttendance = data.reduce((acc, item) => ({
        present: acc.present + item.present,
        total: acc.total + item.total_students
    }), { present: 0, total: 0 });

    const overallPercentage = totalAttendance.total > 0
        ? ((totalAttendance.present / totalAttendance.total) * 100).toFixed(2)
        : 0;

    return (
        <Card elevation={2}>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" fontWeight="bold">
                        Daily Attendance Summary
                    </Typography>
                    <Tooltip title="Refresh">
                        <IconButton size="small" onClick={fetchData} disabled={loading}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Box>

                {data.length === 0 ? (
                    <Alert severity="info">No attendance data available for today</Alert>
                ) : (
                    <>
                        {/* Overall Stats */}
                        <Box mb={3} p={2} sx={{ backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant="body1" fontWeight="bold">
                                    Overall Attendance
                                </Typography>
                                <Typography 
                                    variant="h5" 
                                    fontWeight="bold" 
                                    color={getAttendanceColor(Number(overallPercentage))}
                                >
                                    {overallPercentage}%
                                </Typography>
                            </Box>
                            <LinearProgress 
                                variant="determinate" 
                                value={Number(overallPercentage)} 
                                sx={{ 
                                    height: 10, 
                                    borderRadius: 5,
                                    backgroundColor: '#e0e0e0',
                                    '& .MuiLinearProgress-bar': {
                                        backgroundColor: getAttendanceColor(Number(overallPercentage))
                                    }
                                }}
                            />
                            <Box display="flex" justifyContent="space-between" mt={1}>
                                <Typography variant="caption" color="text.secondary">
                                    Present: {totalAttendance.present}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Total: {totalAttendance.total}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Branch-wise Details */}
                        {data.map((item, index) => (
                            <Box key={index} mb={2} p={2} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography variant="body2" fontWeight="bold">
                                        {item.branch_name}
                                    </Typography>
                                    <Chip 
                                        label={`${item.percentage}%`}
                                        size="small"
                                        sx={{ 
                                            backgroundColor: getAttendanceColor(item.percentage),
                                            color: 'white',
                                            fontWeight: 'bold'
                                        }}
                                    />
                                </Box>
                                <Box display="flex" gap={1} flexWrap="wrap">
                                    <Chip 
                                        label={`Present: ${item.present}`} 
                                        size="small" 
                                        color="success" 
                                        variant="outlined" 
                                    />
                                    <Chip 
                                        label={`Absent: ${item.absent}`} 
                                        size="small" 
                                        color="error" 
                                        variant="outlined" 
                                    />
                                    <Chip 
                                        label={`Leave: ${item.on_leave}`} 
                                        size="small" 
                                        color="warning" 
                                        variant="outlined" 
                                    />
                                </Box>
                            </Box>
                        ))}
                    </>
                )}

                <Typography variant="caption" color="text.secondary" display="block" mt={2}>
                    Last updated: {lastRefresh.toLocaleTimeString()}
                </Typography>
            </CardContent>
        </Card>
    );
};

export default DailyAttendanceSummary;
